import {
  CURRENCY_TYPE_ENUM,
  ECOM_EXTERNAL_CONNECTION_TYPE_ENUM,
  STORE_HOSTING_URL
} from '../../../../config/constants';

const getVariantOptionSuffix = (optionName) => {
  let sizeSuffix = '';
  const sizeRegex = /\b(?:pack of|set of)\s+(\d+)\b|\b(\d+)\s+(?:pack|set)\b/i;
  const shapeMatch = optionName
    .toLowerCase()
    .match(/\b(square|circle|round)\b/);

  if (shapeMatch && shapeMatch[1]) {
    sizeSuffix += ` - ${shapeMatch[1]}`;
  }

  const match = optionName.toLowerCase().match(sizeRegex);
  const quantity = match ? match[1] || match[2] : null;
  if (quantity) {
    sizeSuffix += ` x ${quantity}`;
  }

  return sizeSuffix;
};

const config = {
  title: 'Duplium',
  domain: 'https://www.duplium.com/',
  description: 'Connect to Duplium to sell print on demand products.',
  pkEcomExternalConnectionType: ECOM_EXTERNAL_CONNECTION_TYPE_ENUM.DUPLIUM,
  currencyType: CURRENCY_TYPE_ENUM.CAD,
  routeValue: 'duplium',
  products: {
    dashboardUrl: null,
    filterOptions: [],
    productUrlLabel: 'View in Printify',
    getProductUrl: (ecomVendorProduct) => {
      const dupliumConnection = ecomVendorProduct?.ecomVendorProductExternalIds?.find(
        (i) =>
          i.fkEcomExternalConnectionType.toString() ===
          ECOM_EXTERNAL_CONNECTION_TYPE_ENUM.DUPLIUM.toString()
      );
      if (dupliumConnection) {
        return `https://printify.com/app/product-details/${dupliumConnection.externalId}`;
      }
      return null;
    },
    getProductUrlById: (id) =>
      id ? `https://printify.com/app/product-details/${id}` : null,
    getAdminProductUrl: (ecomVendorProduct) => {
      const dupliumConnection = ecomVendorProduct?.ecomVendorProductExternalIds?.find(
        (i) =>
          i.fkEcomExternalConnectionType.toString() ===
          ECOM_EXTERNAL_CONNECTION_TYPE_ENUM.DUPLIUM.toString()
      );
      if (dupliumConnection) {
        return `${STORE_HOSTING_URL}/admin/products/${dupliumConnection.fkEcomExternalProduct}`;
      }
      return null;
    },
    restrictions: {
      canChangeImages: true,
      canDuplicate: false,
      canCreateVariants: false,
      canCreateInputs: false,
      canManageInventory: false,
      canManageShipping: false,
      canChangeCurrency: false,
      canChangePrice: false,
      canChangeSku: false,
      canChangePrintTemplate: true,
      canCustomizeDesign: true
    },
    inputSections: [
      {
        label: 'External connection',
        inputs: [
          {
            label: 'Printify Product ID',
            dataValue: 'externalId',
            description: 'The product catalog ID within Printify.',
            type: 'select',
            required: true,
            linkTitle: 'View in Printify',
            getNavLink: (printifyProduct) =>
              printifyProduct
                ? `https://printify.com/app/products/${
                    printifyProduct.value
                  }/${printifyProduct.brand
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')}/${printifyProduct.label
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9\s-]/g, '')}`
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-')
                : null
          }
        ]
      }
    ],
    syncOptions: [
      {
        label: 'Title',
        dataValue: 'name'
      },
      {
        label: 'Description',
        dataValue: 'description',
        isVendorSync: true
      },
      {
        label: 'Images',
        dataValue: 'images',
        description:
          'New images will be added alongside existing ones and will not replace or remove any current images.'
      }
    ],
    formStateThatRequiresSync: []
  },
  shippingOptions: [
    {
      value: '1',
      label: 'Standard',
      description: '2-5 business days'
    },
    {
      value: '2',
      label: 'Priority',
      description: '2-3 business days'
    },
    {
      value: '3',
      label: 'Express',
      description: '2-3 business days (incl. production)'
    },
    {
      value: '4',
      label: 'Economy',
      description: '4-8 business days'
    }
  ],
  importConfig: {
    fields: [
      {
        label: 'SKU / Product #',
        isRequired: true,
        dataValue: 'sku'
      },
      {
        label: 'Description / Caption',
        dataValue: 'description'
      },
      {
        label: 'style/model',
        dataValue: 'style',
        description: 'Required for new products'
      },
      {
        label: 'COLOR',
        dataValue: 'color',
        isRequired: true
      },
      {
        label: 'SIZE',
        dataValue: 'size',
        isRequired: true
      },
      {
        label: 'Price',
        dataValue: 'price'
      },
      {
        label: 'Weight',
        dataValue: 'weight'
      }
    ],
    onParseCsv: (data, ecomExternalProducts) => {
      const productData = data.reduce((r, c) => {
        const existingProduct = ecomExternalProducts.find((p) =>
          p.ecomExternalProductVariants.some(
            (v) => v.externalId === c.sku.toString()
          )
        );

        const productName = c.description.replaceAll('_', ' ');
        let sizeSuffix = getVariantOptionSuffix(productName);
        if (!sizeSuffix) {
          sizeSuffix = getVariantOptionSuffix(c.description);
        }

        const variantData = {
          sku: c.sku,
          externalId: c.sku,
          price: c.price
            ? Math.round(Number(c.price.trim().slice(1)) * 100)
            : null,
          weight: c.weight ? Number(c.weight.trim()) : null,
          options: [
            {
              Name: 'Color',
              value: c.color
            },
            {
              Name: 'Size',
              value: `${c.size}${sizeSuffix}`
            }
          ]
        };

        const product = r.find(
          (p) =>
            (p.style &&
              c.style &&
              p.style?.toLowerCase() === c.style?.trim().toLowerCase()) ||
            (existingProduct &&
              p.pkEcomExternalProduct === existingProduct.pkEcomExternalProduct)
        );
        if (product) {
          product.variants.push(variantData);
        }
        else if (existingProduct || !!c.style) {
          r.push({
            key: c.style || variantData.sku,
            name: productName,
            ...existingProduct,
            description: existingProduct?.description || c.description,
            model: c.style || existingProduct?.model || '',
            style: c.style?.trim(),
            variants: [variantData]
          });
        }

        return r;
      }, []);

      return productData;
    }
  }
};

export { config };
